/**
 * Copy + routing helpers for the Google Search paid landing page.
 * Keep this module free of React so unit tests stay cheap and deterministic.
 */

export const GOOGLE_SEARCH_LP_PATH = '/lp/google-search';
export const GOOGLE_SEARCH_SIGNUP_EXPERIENCE_EXPERIMENT_NAME = 'google_search_signup_experience_v2';
export const GOOGLE_SEARCH_SIGNUP_EXPERIENCE_FEATURE_FLAG = 'google_search_signup_experience_v2_variant';
export const GOOGLE_SEARCH_SIGNUP_EXPERIENCE_PREVIEW_PARAM = 'signupExperience';

export const GOOGLE_SEARCH_SIGNUP_EXPERIENCE_VARIANTS = {
  control: {
    id: 'control',
    contactButtonLabel: 'CONTINUE WITH EMAIL ->',
    footer: 'BotSpot requires a paid plan. Review pricing before purchase.',
  },
  auth_first: {
    id: 'auth_first',
    footer: 'BotSpot requires a paid plan. Review pricing before purchase.',
  },
};

export const GOOGLE_SEARCH_JOBS = {
  'schwab-ai-control': {
    id: 'schwab-ai-control',
    badge: 'For Schwab users',
    headline: 'Analyze your Schwab charts with AI',
    headlineAccent: 'with AI',
    subhead:
      'Describe a stock or chart in plain English. BotSpot shows evidence and risk so you can decide what to do next.',
    proof: [
      'Works with your connected Schwab account context',
      'Shows chart and research evidence before you act',
      'You approve every next step. Nothing trades on its own',
    ],
    defaultPrompt:
      'I use Schwab. Help me analyze a stock or chart, show the evidence and risk, and let me decide what to do',
    authFirst: {
      evidenceLabel: 'AI chart workflow',
      evidenceDescription: 'Ask in plain English. Review the chart, sources, and risk.',
      evidenceMeta: 'One conversation',
      steps: ['Ask with AI', 'Review evidence', 'Approve the next step'],
    },
    utmCampaigns: ['b2c_schwab_ai_control_2026q3_r3_daily'],
  },
  'tos-scanner': {
    id: 'tos-scanner',
    badge: 'For thinkorswim users',
    headline: 'Describe a thinkorswim scan in plain English',
    headlineAccent: 'in plain English',
    subhead:
      'Tell BotSpot the scan you want. Get current matches with clear pass or fail reasons instead of maintaining Stock Hacker scripts alone.',
    proof: [
      'Built for thinkorswim and Stock Hacker style workflows',
      'Explains why each symbol passed or failed',
      'Keeps you in the command seat before any trade',
    ],
    defaultPrompt:
      'I use thinkorswim. Let me describe a stock scan in plain English, find current matches, and explain why each symbol passed or failed',
    authFirst: {
      evidenceLabel: 'AI scanner workflow',
      evidenceDescription: 'Describe the scan. Review current matches and pass or fail reasons.',
      evidenceMeta: 'Plain English',
      steps: ['Describe the scan', 'Review matches', 'Approve the next step'],
    },
    utmCampaigns: ['b2c_thinkorswim_scanner_2026q3_r3_daily'],
  },
  'portfolio-monitor': {
    id: 'portfolio-monitor',
    badge: 'For Schwab portfolios',
    headline: 'Monitor your Schwab portfolio with AI',
    headlineAccent: 'with AI',
    subhead:
      'Connect your holdings context and get clear explanations of news, filings, and risk changes that deserve attention.',
    proof: [
      'Focuses on your holdings, not random stock tips',
      'Explains why a change matters with sources',
      'Independent from Schwab. You keep control',
    ],
    defaultPrompt:
      'Connect my Schwab portfolio and explain which holdings, news, filings, and risks deserve attention with sources',
    authFirst: {
      evidenceLabel: 'Portfolio review workflow',
      evidenceDescription: 'Focus AI research on your holdings, news, filings, and risk.',
      evidenceMeta: 'Your holdings',
      steps: ['Connect context', 'Review changes', 'Approve the next step'],
    },
    utmCampaigns: ['b2c_schwab_portfolio_monitor_2026q3_r3_daily'],
  },
  'schwab-copilot': {
    id: 'schwab-copilot',
    badge: 'Schwab trade review',
    headline: 'Review your Schwab setup with AI',
    headlineAccent: 'with AI',
    subhead:
      'Describe the setup you already trade. Inspect chart evidence and risk before you decide.',
    proof: [
      'Built for one current setup, not a long bot-building project',
      'Shows chart evidence and risk context first',
      'You approve every action',
    ],
    defaultPrompt:
      'Connect my Schwab account, analyze my current setup, and show chart evidence and risk for my review',
    authFirst: {
      evidenceLabel: 'AI setup review',
      evidenceDescription: 'Describe the setup you trade. Review chart evidence and risk.',
      evidenceMeta: 'Your setup',
      steps: ['Describe the setup', 'Review evidence', 'Approve the trade'],
    },
    utmCampaigns: ['b2c_schwab_copilot_2026q3_r3_daily'],
  },
};

const CAMPAIGN_TO_JOB = Object.fromEntries(
  Object.values(GOOGLE_SEARCH_JOBS).flatMap((job) =>
    (job.utmCampaigns || []).map((campaign) => [campaign, job.id])
  )
);

export function normalizeGoogleSearchSignupExperienceVariant(rawVariant) {
  if (rawVariant && GOOGLE_SEARCH_SIGNUP_EXPERIENCE_VARIANTS[rawVariant]) {
    return rawVariant;
  }
  return 'control';
}

export function resolveGoogleSearchJobId({ job, utmCampaign, prompt } = {}) {
  const explicit = typeof job === 'string' ? job.trim().toLowerCase() : '';
  if (explicit && GOOGLE_SEARCH_JOBS[explicit]) {
    return explicit;
  }

  const campaign = typeof utmCampaign === 'string' ? utmCampaign.trim() : '';
  if (campaign && CAMPAIGN_TO_JOB[campaign]) {
    return CAMPAIGN_TO_JOB[campaign];
  }

  const promptText = typeof prompt === 'string' ? prompt.toLowerCase() : '';
  if (promptText.includes('scan') || promptText.includes('thinkorswim')) {
    if (promptText.includes('portfolio') || promptText.includes('holding')) {
      return 'portfolio-monitor';
    }
    if (promptText.includes('setup') || promptText.includes('orb') || promptText.includes('vwap')) {
      return promptText.includes('scan') ? 'tos-scanner' : 'schwab-copilot';
    }
    return 'tos-scanner';
  }
  if (promptText.includes('portfolio') || promptText.includes('holding')) {
    return 'portfolio-monitor';
  }
  if (promptText.includes('setup') || promptText.includes('trade review')) {
    return 'schwab-copilot';
  }

  return 'schwab-ai-control';
}

export function getGoogleSearchJob(jobId) {
  return GOOGLE_SEARCH_JOBS[jobId] || GOOGLE_SEARCH_JOBS['schwab-ai-control'];
}

export function getGoogleSearchSignupExperienceVariantConfig(variantId) {
  return (
    GOOGLE_SEARCH_SIGNUP_EXPERIENCE_VARIANTS[
      normalizeGoogleSearchSignupExperienceVariant(variantId)
    ] || GOOGLE_SEARCH_SIGNUP_EXPERIENCE_VARIANTS.control
  );
}

export function buildGoogleSearchLpPath({ job, prompt, extraParams } = {}) {
  const params = new URLSearchParams();
  if (job) params.set('job', job);
  if (prompt) params.set('prompt', prompt);
  if (extraParams && typeof extraParams === 'object') {
    Object.entries(extraParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    });
  }
  const query = params.toString();
  return query ? `${GOOGLE_SEARCH_LP_PATH}?${query}` : GOOGLE_SEARCH_LP_PATH;
}
